import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, AlertTriangle, CalendarClock, UserX } from "lucide-react";
import { AdminTitle } from "./AdminLayout";
import { InquiryDetail } from "./InquiryDetail";
import { INQ_STATUS, StatusPill, AssigneePill, FollowPill } from "./inquiryUi";
import { http, apiError, formatDateTime, DIVISIONS } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../components/ui/sheet";

const FILTERS = ["assigned", "division", "status", "follow", "q"];

export default function InquiryManager() {
  const { user } = useAuth();
  const owner = user?.role === "owner";
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const query = Object.fromEntries(FILTERS.filter((k) => params.get(k)).map((k) => [k, params.get(k)]));
  const openId = params.get("id");

  const { data, isLoading } = useQuery({ queryKey: ["inquiries", query], queryFn: async () => (await http.get("/admin/inquiries", { params: query })).data });
  const { data: teamData } = useQuery({ queryKey: ["team"], queryFn: async () => (await http.get("/admin/inquiries/team")).data });
  const { data: fu } = useQuery({ queryKey: ["followups"], queryFn: async () => (await http.get("/admin/inquiries/followups")).data });
  const { data: detail } = useQuery({ queryKey: ["inquiry", openId], enabled: !!openId, retry: 0,
    queryFn: async () => (await http.get(`/admin/inquiries/${openId}`)).data });
  const items = Array.isArray(data?.items) ? data.items : [];
  const team = teamData?.items || [];
  const unassigned = useMemo(() => items.filter((i) => !i.assigned_to).length, [items]);

  const set = (patch) => {
    const n = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    setParams(n, { replace: true });
  };
  const refresh = (doc) => {
    if (doc?.id) qc.setQueryData(["inquiry", doc.id], doc);
    ["inquiries", "followups", "admin-stats"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };
  const assign = async (i, uid) => {
    try {
      const { data: doc } = await http.post(`/admin/inquiries/${i.id}/assign`, { user_id: uid || null });
      toast.success(uid ? `Assigned to ${doc.assigned_name}. Notified by email.` : "Unassigned");
      refresh(doc);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const setStatus = async (i, status) => {
    try {
      const { data: doc } = await http.patch(`/admin/inquiries/${i.id}`, { status });
      toast.success("Status updated");
      refresh(doc);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const summary = [
    [AlertTriangle, "Overdue follow-ups", fu?.overdue?.length ?? 0, "overdue", "text-rose-300", "border-rose-500/50"],
    [CalendarClock, "Upcoming (7 days)", fu?.upcoming?.length ?? 0, "upcoming", "text-amber-200", "border-amber-400/40"],
  ];

  return (
    <div data-testid="admin-inquiries">
      <AdminTitle title="Inquiry management" sub={owner ? "Assign every inquiry, track status and never miss a follow-up." : "Inquiries assigned to you."} />

      <div className={`grid gap-3 mb-6 ${owner ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
        {owner && (
          <button onClick={() => set({ assigned: params.get("assigned") === "unassigned" ? "" : "unassigned" })} className={`text-left border p-5 bg-[var(--ink-2)] transition-colors ${params.get("assigned") === "unassigned" ? "border-rose-400" : "border-rose-500/40 hover:border-rose-400"}`} data-testid="summary-unassigned">
            <UserX className="w-5 h-5 text-rose-300" />
            <p className="font-display text-4xl mt-2">{params.get("assigned") === "unassigned" ? items.length : unassigned}</p>
            <p className="text-xs uppercase tracking-[0.14em] text-slate-300 mt-1">Unassigned</p>
          </button>
        )}
        {summary.map(([Icon, label, n, key, tc, bc]) => (
          <button key={key} onClick={() => set({ follow: params.get("follow") === key ? "" : key })} className={`text-left border p-5 bg-[var(--ink-2)] transition-colors ${bc} ${params.get("follow") === key ? "!border-[var(--gold)]" : ""}`} data-testid={`summary-${key}`}>
            <Icon className={`w-5 h-5 ${tc}`} />
            <p className="font-display text-4xl mt-2">{n}</p>
            <p className="text-xs uppercase tracking-[0.14em] text-slate-300 mt-1">{label}</p>
          </button>
        ))}
      </div>

      <div className="flex flex-col xl:flex-row gap-3 mb-6">
        <form className="relative flex-1" onSubmit={(e) => { e.preventDefault(); set({ q: q.trim() }); }}>
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input className="field !py-2.5 !pl-10" placeholder="Search name, email, asset, stock # or INQ ref… (Enter)" value={q} onChange={(e) => setQ(e.target.value)} onBlur={() => set({ q: q.trim() })} data-testid="inquiry-search-input" />
        </form>
        {owner && (
          <select className="field xl:!w-48 !py-2.5" value={params.get("assigned") || ""} onChange={(e) => set({ assigned: e.target.value })} data-testid="filter-assigned">
            <option value="">Anyone</option><option value="unassigned">Unassigned</option>
            {team.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
        <select className="field xl:!w-48 !py-2.5" value={params.get("division") || ""} onChange={(e) => set({ division: e.target.value })} data-testid="filter-division">
          <option value="">All divisions</option>
          {DIVISIONS.map((d) => <option key={d.key} value={d.name}>{d.name}</option>)}
        </select>
        <select className="field xl:!w-48 !py-2.5" value={params.get("status") || ""} onChange={(e) => set({ status: e.target.value })} data-testid="filter-status">
          <option value="">All statuses</option>
          {Object.entries(INQ_STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
        </select>
      </div>

      {isLoading ? <p className="text-slate-400">Loading…</p> : !items.length ? (
        <p className="border border-dashed border-white/20 p-12 text-center text-slate-300" data-testid="inquiries-empty">{owner ? "No inquiries match these filters." : "No inquiries are assigned to you right now."}</p>
      ) : (
        <div className="border hairline divide-y divide-white/10" data-testid="inquiry-list">
          {items.map((i) => (
            <div key={i.id} className={`p-4 md:p-5 grid lg:grid-cols-[1fr_auto] gap-4 items-center bg-[var(--ink-2)] hover:bg-[var(--ink-3)] transition-colors ${!i.assigned_to ? "border-l-2 border-l-rose-400" : ""}`} data-testid={`inquiry-row-${i.id}`}>
              <button className="text-left min-w-0" onClick={() => set({ id: i.id })} data-testid={`inquiry-open-${i.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-gold">{i.ref}</span>
                  <StatusPill status={i.status} /><AssigneePill item={i} /><FollowPill item={i} />
                </div>
                <p className="mt-2 text-slate-100 font-medium truncate">{i.name} <span className="text-slate-400 font-normal">· {i.email}</span></p>
                <p className="text-sm text-slate-300 truncate">{i.asset_title}{i.division ? ` · ${i.division}` : ""}</p>
                <p className="text-xs text-slate-400 mt-1">Submitted {formatDateTime(i.created_at)}</p>
              </button>
              <div className="flex flex-wrap gap-2">
                {owner && (
                  <select className="field !w-auto !py-2 !text-xs" value={i.assigned_to || ""} onChange={(e) => assign(i, e.target.value)} data-testid={`assign-select-${i.id}`}>
                    <option value="">Unassigned</option>
                    {team.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
                <select className="field !w-auto !py-2 !text-xs" value={i.status} onChange={(e) => setStatus(i, e.target.value)} data-testid={`status-select-${i.id}`}>
                  {Object.entries(INQ_STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={!!openId} onOpenChange={(o) => !o && set({ id: "" })}>
        <SheetContent side="right" className="w-full sm:max-w-xl bg-[var(--ink)] border-white/10 overflow-y-auto" data-testid="inquiry-sheet">
          <SheetHeader className="sr-only"><SheetTitle>Inquiry details</SheetTitle><SheetDescription>Assignment, status, follow-up and notes</SheetDescription></SheetHeader>
          {detail ? (
            <InquiryDetail key={`${detail.id}-${detail.follow_up_at}`} item={detail} team={team} onChange={refresh} onDeleted={() => { set({ id: "" }); refresh(); }} />
          ) : <p className="text-slate-400 pt-10">Loading… (or this inquiry is not assigned to you)</p>}
        </SheetContent>
      </Sheet>
    </div>
  );
}
